import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { setOptionComposite, store } from "../../store";
import {
    CLICKTOPAY_REGLEMENT,
    ClickToPayConfig,
    INITIAL_CLICKTOPAY,
    INITIAL_PAYLINE,
    INITIAL_YAVIN,
    PAYLINE_REGLEMENT,
    PaylineConfig,
    YAVIN_REGLEMENTS,
    YavinConfig,
} from "./types";

type UsePaymentConfigsArgs = {
    selectedRows: any;
    isCreditCardActive: boolean;
};

export function usePaymentConfigs({
    selectedRows,
    isCreditCardActive,
}: UsePaymentConfigsArgs) {
    const { optionComposite } = useSnapshot(store);

    const reglement =
        selectedRows.length > 0 ? selectedRows[0]?.reglement : null;
    const compositeProducts = selectedRows[0]?.["composite Products"] || {};
    const apkId = selectedRows[0]?.iuudApk || selectedRows[0]?.uuid;

    const isPayline = reglement === PAYLINE_REGLEMENT;
    const isClickToPay = reglement === CLICKTOPAY_REGLEMENT;
    const isYavin = YAVIN_REGLEMENTS.includes(reglement);

    const [paylineConfig, setPaylineConfig] = useState<PaylineConfig>({
        ...INITIAL_PAYLINE,
        ...(compositeProducts?.payline || {}),
        api: compositeProducts?.payline?.api || INITIAL_PAYLINE.api,
    });
    const [clickToPayConfig, setClickToPayConfig] = useState<ClickToPayConfig>({
        ...INITIAL_CLICKTOPAY,
        ...(compositeProducts?.CLICKTOPAY || {}),
    });
    const [yavinConfig, setYavinConfig] = useState<YavinConfig>({
        ...INITIAL_YAVIN,
        ...(compositeProducts?.YAVIN || {}),
    });

    // Reset configs when the selected APK changes
    useEffect(() => {
        setPaylineConfig({
            ...INITIAL_PAYLINE,
            ...(compositeProducts?.payline || {}),
            api: compositeProducts?.payline?.api || INITIAL_PAYLINE.api,
        });
        setClickToPayConfig({
            ...INITIAL_CLICKTOPAY,
            ...(compositeProducts?.CLICKTOPAY || {}),
        });
        setYavinConfig({
            ...INITIAL_YAVIN,
            ...(compositeProducts?.YAVIN || {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apkId]);

    // Sync active provider config into the global store
    useEffect(() => {
        if (!isCreditCardActive) return;
        const merged: any = { ...optionComposite };
        if (isPayline) merged.payline = paylineConfig;
        if (isClickToPay) merged.CLICKTOPAY = clickToPayConfig;
        if (isYavin) merged.YAVIN = yavinConfig;
        setOptionComposite(merged);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isCreditCardActive,
        isPayline,
        isClickToPay,
        isYavin,
        paylineConfig,
        clickToPayConfig,
        yavinConfig,
    ]);

    const savePayline = (config: PaylineConfig) => setPaylineConfig(config);
    const saveClickToPay = (config: ClickToPayConfig) =>
        setClickToPayConfig(config);
    const saveYavin = (config: YavinConfig) => setYavinConfig(config);

    return {
        isPayline,
        isClickToPay,
        isYavin,
        hasConfigurableProvider: isPayline || isClickToPay || isYavin,
        paylineConfig,
        clickToPayConfig,
        yavinConfig,
        savePayline,
        saveClickToPay,
        saveYavin,
    };
}
